import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "../context/CartContext";
import { weeklyLease } from "../lib/lease";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "./ui/sheet";

export default function CartDrawer() {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-md" data-testid="cart-drawer">
        <SheetHeader className="border-b border-line pb-4">
          <SheetTitle className="font-display text-2xl font-black tracking-tight text-ink">
            Your cart {count > 0 && <span className="text-crimson">({count})</span>}
          </SheetTitle>
          <SheetDescription className="text-sm text-muted-foreground">
            Buy outright with card or pay over time with Affirm at checkout.
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center" data-testid="cart-empty">
            <ShoppingBag className="h-12 w-12 text-line" />
            <p className="font-bold text-ink">Your cart is empty</p>
            <button
              onClick={() => { setOpen(false); navigate("/products"); }}
              data-testid="cart-browse-btn"
              className="rounded-full bg-ink px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-crimson"
            >
              Browse iPhones
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 divide-y divide-line overflow-y-auto py-2" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 py-4" data-testid={`cart-item-${i.key}`}>
                  <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-line bg-alt">
                    {i.image && <img src={i.image} alt={i.name} className="h-full w-full object-contain p-1" />}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-sm font-bold text-ink">{i.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {[i.condition, i.color, i.storage].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      <button
                        onClick={() => removeItem(i.key)}
                        data-testid={`cart-remove-${i.key}`}
                        aria-label="Remove item"
                        className="text-muted-foreground transition-colors hover:text-crimson"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQty(i.key, i.quantity - 1)}
                          data-testid={`cart-dec-${i.key}`}
                          aria-label="Decrease quantity"
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink transition-colors hover:border-ink"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <span className="w-6 text-center text-sm font-bold text-ink" data-testid={`cart-qty-${i.key}`}>{i.quantity}</span>
                        <button
                          onClick={() => updateQty(i.key, i.quantity + 1)}
                          disabled={i.max_qty != null && i.quantity >= i.max_qty}
                          data-testid={`cart-inc-${i.key}`}
                          aria-label="Increase quantity"
                          className="flex h-7 w-7 items-center justify-center rounded-lg border border-line text-ink transition-colors hover:border-ink disabled:opacity-40"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="font-display text-base font-black text-ink">${(i.price * i.quantity).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line pt-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-muted-foreground">Subtotal</span>
                <span className="font-display text-xl font-black text-ink" data-testid="cart-subtotal">${subtotal.toLocaleString()}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Sales tax calculated at checkout. Free UPS shipping.</p>
              <p className="mt-1 text-xs font-semibold text-crimson">Or lease from ${weeklyLease(subtotal)}/wk with Payvantage.</p>
              <button
                onClick={goCheckout}
                data-testid="cart-checkout-btn"
                className="mt-4 w-full rounded-full bg-crimson px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-crimson-hover"
              >
                Checkout
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
